import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DialogAlert } from '@/shared/types/dialogAlert';

export const defaultState: DialogAlert[] = [];

const clearAlerts = (state: DialogAlert[], action: PayloadAction<Partial<DialogAlert>>) => {
  const { id, nodeId, index } = action.payload;

  if (id) {
    const filteredState = state.filter((d) => d.id !== id);
    state.splice(0, state.length, ...filteredState);
  } else if (nodeId && index !== undefined) {
    const filteredState = state.filter((d) => !(d.nodeId === nodeId && d.index === index));
    state.splice(0, state.length, ...filteredState);
  } else if (nodeId) {
    const filteredState = state.filter((d) => d.nodeId !== nodeId);
    state.splice(0, state.length, ...filteredState);
  }
  return state;
};

const nodesSlice = createSlice({
  name: 'dialogAlerts',
  initialState: defaultState as DialogAlert[],
  reducers: {
    setDialogAlert: (state: DialogAlert[], action: PayloadAction<DialogAlert>) => {
      const alert = action.payload;
      const index = state.findIndex(
        (d) =>
          d.id === alert.id &&
          d.alertField === alert.alertField &&
          d.cardIndex === alert.cardIndex &&
          d.buttonIndex === alert.buttonIndex
      );

      if (index !== -1) {
        state[index] = alert;
      } else {
        state.push(alert);
      }
    },

    removeDialogAlert: (
      state: DialogAlert[],
      action: PayloadAction<{
        id: string;
        alertField?: string;
        cardIndex?: number;
        buttonIndex?: number;
      }>
    ) => {
      const { id, alertField, cardIndex, buttonIndex } = action.payload;
      if (alertField !== undefined) {
        return state.filter(
          (d) =>
            !(
              d.id === id &&
              d.alertField === alertField &&
              d.cardIndex === cardIndex &&
              d.buttonIndex === buttonIndex
            )
        );
      }
      return state.filter((d) => d.id !== id);
    },

    /** @deprecated Use setDialogAlert / removeDialogAlert instead */
    updateDialogAlerts: (state: DialogAlert[], action: PayloadAction<DialogAlert>) => {
      const dialogAlert = action.payload;

      if (!dialogAlert?.body) {
        return state.filter(
          (d) => !(d.id === dialogAlert.id && d.alertField === dialogAlert.alertField)
        );
      }

      const index = state.findIndex(
        (d) => d.id === dialogAlert.id && d.alertField === dialogAlert.alertField
      );

      if (index !== -1) {
        state[index] = dialogAlert;
      } else {
        state.push(dialogAlert);
      }
    },

    clearDialogAlerts: (state: DialogAlert[], action: PayloadAction<Partial<DialogAlert>>) =>
      clearAlerts(state, action),
  },
  // Clear dialog alerts of type error when changing dialog_id. Since when we show error is not possible to save the dialog, we only keep the warnings.
  extraReducers: (builder) => {
    builder.addCase('session/setDialogId', (state: DialogAlert[]) => {
      const errors = state.filter((dialog) => dialog.alertType === 'error');
      errors.forEach((error) =>
        clearAlerts(state, { payload: error, type: 'session/setDialogId' })
      );
    });
    builder.addCase('session/resetBrain', () => {
      return [];
    });
    // Clean up alerts when a requisite is removed
    builder.addMatcher(
      (action): action is PayloadAction<{ nodeId: string; index: number }> =>
        action.type === 'nodes/removeRequisite',
      (state: DialogAlert[], action) => {
        const { nodeId, index } = action.payload;
        clearAlerts(state, { payload: { nodeId, index }, type: 'nodes/removeRequisite' });
      }
    );
    // Clean up alerts when a node is removed
    builder.addMatcher(
      (action): action is PayloadAction<{ nodeId: string }> => action.type === 'nodes/removeNode',
      (state: DialogAlert[], action) => {
        const { nodeId } = action.payload;
        clearAlerts(state, { payload: { nodeId }, type: 'nodes/removeNode' });
      }
    );
    // Clean up alerts when a node is converted (intent <-> event); the toolkit re-validates on mount
    builder.addMatcher(
      (action): action is PayloadAction<{ nodeId: string }> => action.type === 'nodes/convertNode',
      (state: DialogAlert[], action) => {
        const { nodeId } = action.payload;
        clearAlerts(state, { payload: { nodeId }, type: 'nodes/convertNode' });
      }
    );
  },
});

export const { updateDialogAlerts, clearDialogAlerts, setDialogAlert, removeDialogAlert } =
  nodesSlice.actions;

export default nodesSlice.reducer;
